/**
 * mapGen.ts
 * Pure map-generation function with zero browser-API dependencies.
 * Safe to import from a Web Worker.
 *
 * All randomness is derived deterministically from `seed`, `width`, `height`.
 * The function returns a self-contained data blob; no DOM, no window, no
 * localStorage access anywhere in this module or its imports.
 */

import { TerrainType, type GridPosition, type VenueLayout, type CorporateHQLayout } from '../types';
import { GROUND_LEVELS, ISO_TILE_WIDTH, ISO_TILE_HEIGHT, ELEVATION_STEP } from '../constants';

// ── Private helpers ──────────────────────────────────────────────────────────

/** Deterministic 0..1 hash from two integer coordinates + a seed. */
function hash2(x: number, y: number, seed: number): number {
  let h = (x * 374761393 + y * 668265263 + seed * 1013904223) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967295;
}

/** Minimal seeded PRNG (xorshift32). */
function makeRng(seed: number) {
  let s = (seed >>> 0) || 1;
  const next = (): number => {
    s ^= s << 13; s ^= s >>> 17; s ^= s << 5;
    s = s >>> 0;
    return s / 4294967296;
  };
  return {
    next,
    int: (lo: number, hi: number) => lo + Math.floor(next() * (hi - lo + 1)),
  };
}

// ── Public output type ───────────────────────────────────────────────────────

export interface MapGenData {
  terrain:             TerrainType[];  // flat [row * width + col]
  variant:             number[];       // per-tile colour variant (0-3)
  hillCol:             number;
  hillRow:             number;
  hillRadius:          number;
  venueLayouts:        VenueLayout[];
  corporateHQ:         CorporateHQLayout;
  bandSpawnPositions:  GridPosition[];
}

// ── Pure generator ───────────────────────────────────────────────────────────

export function generateMapData(seed: number, width: number, height: number): MapGenData {
  const idx = (col: number, row: number) => row * width + col;
  const inBounds = (col: number, row: number) => col >= 0 && col < width && row >= 0 && row < height;

  const terrain = new Array<TerrainType>(width * height).fill(TerrainType.Grass);
  const variant = new Array<number>(width * height).fill(0);

  const set = (col: number, row: number, t: TerrainType): void => {
    if (inBounds(col, row)) terrain[idx(col, row)] = t;
  };

  const rng = makeRng(seed);

  const cx = Math.floor(width  / 2);
  const cy = Math.floor(height / 2);

  // Beacon tiles: (cx-1,cy-1) to (cx,cy) — used for isBeacon & isPassable checks below
  const beaconC = cx - 1;
  const beaconR = cy - 1;

  const isBeacon = (col: number, row: number) =>
    col >= beaconC && col <= beaconC + 1 && row >= beaconR && row <= beaconR + 1;

  const isPassable = (col: number, row: number) => {
    if (isBeacon(col, row)) return false;
    const t = terrain[idx(col, row)];
    return t === TerrainType.Grass || t === TerrainType.Path || t === TerrainType.Corporate;
  };

  // ── Seeded layout parameters ─────────────────────────────────────────────
  const boundRx    = Math.floor(cx * 0.85) + rng.int(-2, 2);
  const boundRy    = Math.floor(cy * 0.80) + rng.int(-2, 2);
  const boundNoise = 0.24 + rng.next() * 0.08;

  const pondCol = (cx + 20) + rng.int(-2, 6);
  const pondRow = (cy + 14) + rng.int(-2, 4);
  const pondRx  = 7 + rng.next() * 3;
  const pondRy  = 5 + rng.next() * 3;

  let hillCol    = (cx + 24) + rng.int(-4, 4);
  let hillRow    = (cy - 16) + rng.int(-4, 4);
  let hillRadius = 8 + rng.int(-2, 2);

  const ambientCount = rng.int(3, 6);
  const ambZones = [
    [cx - 30, cx - 20, cy - 20, cy - 10],
    [cx + 16, cx + 30, cy - 20, cy -  8],
    [cx - 30, cx - 20, cy + 12, cy + 24],
  ];
  const ambientBuildings: Array<{col: number; row: number}> = [];
  for (let i = 0; i < ambientCount; i++) {
    const zone = ambZones[i % ambZones.length];
    ambientBuildings.push({ col: rng.int(zone[0], zone[1]), row: rng.int(zone[2], zone[3]) });
  }

  // 1. Base grass with seeded colour variation.
  for (let row = 0; row < height; row++) {
    for (let col = 0; col < width; col++) {
      const i = idx(col, row);
      terrain[i] = TerrainType.Grass;
      variant[i] = Math.floor(hash2(col, row, seed) * 4);
    }
  }

  // 2. Organic locked boundary.
  for (let row = 0; row < height; row++) {
    for (let col = 0; col < width; col++) {
      const nx = (col - cx) / boundRx;
      const ny = (row - cy) / boundRy;
      const d  = nx * nx + ny * ny + hash2(col, row, seed) * boundNoise;
      if (d > 1) set(col, row, TerrainType.Locked);
    }
  }

  // 3. Streets.
  const lay = (col: number, row: number) => {
    if (inBounds(col, row) && terrain[idx(col, row)] !== TerrainType.Locked) {
      set(col, row, TerrainType.Path);
    }
  };

  for (let col = cx - 32; col <= cx - 18; col++) { lay(col, cy - 1); lay(col, cy); }
  for (let col = cx - 16; col <= cx - 3;  col++) { lay(col, cy - 2); lay(col, cy - 1); }
  for (let col = cx + 3;  col <= cx + 20; col++) { lay(col, cy - 1); lay(col, cy); }
  for (let col = cx + 22; col <= cx + 32; col++) { lay(col, cy);     lay(col, cy + 1); }

  for (let row = cy - 24; row <= cy - 3;  row++) { lay(cx - 2, row); }
  for (let row = cy + 3;  row <= cy + 12; row++) { lay(cx - 2, row); }
  lay(cx - 2, cy + 13); lay(cx - 1, cy + 13); lay(cx, cy + 13);
  for (let row = cy + 14; row <= cy + 22; row++) { lay(cx, row); }

  for (let col = cx - 24; col <= cx - 2;  col++) { lay(col, cy - 8); }
  for (let col = cx + 6;  col <= cx + 18; col++) { lay(col, cy - 8); }
  for (let row = cy - 8;  row <= cy + 10; row++) { lay(cx + 14, row); }
  for (let col = cx - 16; col <= cx + 16; col++) { lay(col, cy + 10); }

  if (rng.next() < 0.55) {
    const extEnd = cx + 16 + rng.int(5, 11);
    for (let col = cx + 16; col <= extEnd; col++) { lay(col, cy + 10); lay(col, cy + 11); }
  }
  if (rng.next() < 0.45) {
    for (let row = cy - 8; row <= cy - 1; row++) { lay(cx + 6, row); }
  }
  if (rng.next() < 0.40) {
    const spurLen = rng.int(4, 8);
    for (let row = cy - 24 - spurLen; row <= cy - 24; row++) { lay(cx - 2, row); }
  }

  // 4. Pond.
  for (let row = 0; row < height; row++) {
    for (let col = 0; col < width; col++) {
      const nx = (col - pondCol) / pondRx;
      const ny = (row - pondRow) / pondRy;
      if (nx * nx + ny * ny < 1 && terrain[idx(col, row)] !== TerrainType.Locked) {
        set(col, row, TerrainType.Water);
      }
    }
  }

  if (rng.next() < 0.45) {
    const p2Col = (cx - 18) + rng.int(-6, 6);
    const p2Row = (cy - 12) + rng.int(-6, 6);
    const p2Rx  = 3 + rng.next() * 4;
    const p2Ry  = 2 + rng.next() * 3;
    for (let row = 0; row < height; row++) {
      for (let col = 0; col < width; col++) {
        const nx = (col - p2Col) / p2Rx;
        const ny = (row - p2Row) / p2Ry;
        if (nx * nx + ny * ny < 1 && terrain[idx(col, row)] === TerrainType.Grass) {
          set(col, row, TerrainType.Water);
        }
      }
    }
  }

  // 5. Venue buildings.
  const fnC = (f: number) => Math.round(cx * f);
  const fnR = (f: number) => Math.round(cy * f);
  const VENUE_ZONES = [
    { minC: cx - 22, maxC: cx - 12, minR: cy - 14, maxR: cy -  6 },
    { minC: cx +  8, maxC: cx + 20, minR: cy - 14, maxR: cy -  6 },
    { minC: cx - 22, maxC: cx - 12, minR: cy +  4, maxR: cy + 14 },
    { minC: cx +  4, maxC: cx + 14, minR: cy +  4, maxR: cy + 14 },
    { minC: cx - 6,             maxC: cx + 6,             minR: cy - fnR(0.55), maxR: cy - fnR(0.50) },
    { minC: cx + fnC(0.48),     maxC: cx + fnC(0.58),     minR: cy - fnR(0.13), maxR: cy - fnR(0.04) },
    { minC: cx - 8,             maxC: cx + 8,             minR: cy + fnR(0.42), maxR: cy + fnR(0.53) },
    { minC: cx - fnC(0.46),     maxC: cx - fnC(0.35),     minR: cy - 6,         maxR: cy + 6         },
  ];

  const venueLayouts: VenueLayout[] = [];

  for (const zone of VENUE_ZONES) {
    let layout: VenueLayout | null = null;
    for (let attempt = 0; attempt < 40 && !layout; attempt++) {
      const bc = rng.int(zone.minC, zone.maxC);
      const br = rng.int(zone.minR, zone.maxR);
      if (!inBounds(bc, br)) continue;
      if (terrain[idx(bc, br)] !== TerrainType.Grass) continue;
      const neighbours = [
        { col: bc,     row: br + 1 },
        { col: bc + 1, row: br     },
        { col: bc - 1, row: br     },
        { col: bc,     row: br - 1 },
      ];
      const access = neighbours.find(n =>
        inBounds(n.col, n.row) &&
        (terrain[idx(n.col, n.row)] === TerrainType.Grass ||
         terrain[idx(n.col, n.row)] === TerrainType.Path),
      );
      if (!access) continue;
      set(bc, br, TerrainType.Building);
      layout = { buildingCol: bc, buildingRow: br, accessCol: access.col, accessRow: access.row };
    }

    if (!layout) {
      const zoneCx = Math.floor((zone.minC + zone.maxC) / 2);
      const zoneCy = Math.floor((zone.minR + zone.maxR) / 2);
      outer: for (let r = 0; r <= 8; r++) {
        for (let dc = -r; dc <= r; dc++) {
          for (let dr = -r; dr <= r; dr++) {
            if (Math.abs(dc) !== r && Math.abs(dr) !== r) continue;
            const bc = zoneCx + dc, br = zoneCy + dr;
            if (!inBounds(bc, br)) continue;
            if (terrain[idx(bc, br)] !== TerrainType.Grass) continue;
            set(bc, br, TerrainType.Building);
            layout = { buildingCol: bc, buildingRow: br, accessCol: bc, accessRow: br + 1 };
            break outer;
          }
        }
      }
    }
    if (layout) venueLayouts.push(layout);
  }

  // 6. Ambient buildings.
  for (const ab of ambientBuildings) {
    if (inBounds(ab.col, ab.row) && terrain[idx(ab.col, ab.row)] === TerrainType.Grass) {
      set(ab.col, ab.row, TerrainType.Building);
    }
  }

  // 7a. Obstacles.
  const nearPath = new Uint8Array(width * height);
  for (let r = 0; r < height; r++) {
    for (let c = 0; c < width; c++) {
      if (terrain[idx(c, r)] !== TerrainType.Path) continue;
      for (let dr = -2; dr <= 2; dr++) {
        for (let dc = -2; dc <= 2; dc++) {
          if (Math.abs(dr) + Math.abs(dc) > 2) continue;
          const nr = r + dr, nc = c + dc;
          if (nr >= 0 && nr < height && nc >= 0 && nc < width) nearPath[idx(nc, nr)] = 1;
        }
      }
    }
  }

  const GEAR_DEPOTS = [
    { col: cx - fnC(0.25), row: cy + fnR(0.08) },
    { col: cx + fnC(0.04), row: cy - fnR(0.19) },
    { col: cx + fnC(0.44), row: cy - fnR(0.08) },
    { col: cx - fnC(0.42), row: cy - fnR(0.33) },
    { col: cx + fnC(0.04), row: cy + fnR(0.28) },
  ];

  const okForObstacle = (c: number, r: number) => {
    if (!inBounds(c, r)) return false;
    if (terrain[idx(c, r)] !== TerrainType.Grass) return false;
    if (nearPath[idx(c, r)]) return false;
    if (Math.abs(c - cx) <= 8 && Math.abs(r - cy) <= 8) return false;
    if (venueLayouts.some(v => Math.abs(c - v.buildingCol) <= 3 && Math.abs(r - v.buildingRow) <= 3)) return false;
    if (c >= width - 14 && r <= 16) return false;
    if (GEAR_DEPOTS.some(d => Math.abs(c - d.col) <= 2 && Math.abs(r - d.row) <= 2)) return false;
    return true;
  };

  const stamp = (c: number, r: number) => {
    if (okForObstacle(c, r)) { set(c, r, TerrainType.Obstacle); nearPath[idx(c, r)] = 1; }
  };

  const ruinCount = rng.int(3, 6);
  for (let f = 0; f < ruinCount; f++) {
    const fw = rng.int(3, 8), fh = rng.int(3, 7);
    const fc = rng.int(cx - 33, cx + 33), fr = rng.int(cy - 24, cy + 24);
    const gapWall = rng.int(0, 3);
    const gapAt   = rng.int(1, Math.max(1, (gapWall < 2 ? fw : fh) - 1));
    for (let dc = 0; dc <= fw; dc++) {
      for (let dr = 0; dr <= fh; dr++) {
        const edge = dc === 0 || dc === fw || dr === 0 || dr === fh;
        if (!edge) continue;
        if (gapWall === 0 && dr === 0  && dc === gapAt) continue;
        if (gapWall === 1 && dr === fh && dc === gapAt) continue;
        if (gapWall === 2 && dc === 0  && dr === gapAt) continue;
        if (gapWall === 3 && dc === fw && dr === gapAt) continue;
        stamp(fc + dc, fr + dr);
      }
    }
  }

  const wallCount = rng.int(10, 16);
  for (let w = 0; w < wallCount; w++) {
    const horiz  = rng.next() > 0.5;
    const startC = rng.int(cx - 33, cx + 33), startR = rng.int(cy - 24, cy + 24);
    const len    = rng.int(5, 14);
    for (let i = 0; i < len; i++) {
      stamp(horiz ? startC + i : startC, horiz ? startR : startR + i);
    }
  }

  const clusterCount = rng.int(6, 12);
  for (let k = 0; k < clusterCount; k++) {
    const cc  = rng.int(cx - 32, cx + 32), cr = rng.int(cy - 23, cy + 23);
    const rad = rng.int(2, 4);
    for (let dr = -rad; dr <= rad; dr++) {
      for (let dc = -rad; dc <= rad; dc++) {
        if (dc * dc + dr * dr > rad * rad + 1) continue;
        if (rng.next() > 0.55) continue;
        stamp(cc + dc, cr + dr);
      }
    }
  }

  const rubbleCount = rng.int(50, 80);
  let placed = 0, tries = 0;
  while (placed < rubbleCount && tries < rubbleCount * 20) {
    tries++;
    const c = rng.int(3, width - 4), r = rng.int(3, height - 4);
    if (!okForObstacle(c, r)) continue;
    set(c, r, TerrainType.Obstacle);
    placed++;
  }

  // 7b. Beacon plaza + 2×2 Beacon building.
  for (let r = cy - 2; r <= cy + 2; r++) {
    for (let c = cx - 2; c <= cx + 2; c++) { set(c, r, TerrainType.Path); }
  }
  for (let r = beaconR; r <= beaconR + 1; r++) {
    for (let c = beaconC; c <= beaconC + 1; c++) { set(c, r, TerrainType.Building); }
  }

  // 8. Corporate HQ.
  const corporateCol = width  - 10;
  const corporateRow = 8;
  const corporateRx  = 8;
  const corporateRy  = 5;

  for (let row = corporateRow - corporateRy - 1; row <= corporateRow + corporateRy + 1; row++) {
    for (let col = corporateCol - corporateRx - 1; col <= corporateCol + corporateRx + 1; col++) {
      if (!inBounds(col, row)) continue;
      const nx = (col - corporateCol) / corporateRx;
      const ny = (row - corporateRow) / corporateRy;
      if (nx * nx + ny * ny <= 1.15) set(col, row, TerrainType.Corporate);
    }
  }

  const bridgeCol = corporateCol - corporateRx - 3;
  for (let col = cx + Math.floor(width * 0.28); col <= bridgeCol; col++) {
    set(col, cy, TerrainType.Path);
    if (col % 3 !== 0) set(col, cy + 1, TerrainType.Path);
  }
  const step = corporateRow < cy ? -1 : 1;
  for (let row = cy; row !== corporateRow + 6; row += step) {
    set(bridgeCol, row, TerrainType.Path);
    if (row % 2 === 0) set(bridgeCol - 1, row, TerrainType.Path);
    if (row % 3 !== 0) set(bridgeCol + 1, row, TerrainType.Path);
    if (row < corporateRow + 18) set(bridgeCol + 3, row, TerrainType.Path);
  }
  for (const row of [corporateRow - 3, corporateRow + 3, corporateRow + 6]) {
    for (let col = bridgeCol; col <= corporateCol - 3; col++) {
      set(col, row, TerrainType.Path);
      if (col % 3 === 0) set(col, row + 1, TerrainType.Path);
    }
  }

  for (let col = bridgeCol; col <= corporateCol - 3; col++) {
    set(col, corporateRow - 4, TerrainType.Corporate);
    set(col, corporateRow + 2, TerrainType.Corporate);
    set(col, corporateRow + 4, TerrainType.Corporate);
    set(col, corporateRow + 7, TerrainType.Corporate);
  }

  for (let row = corporateRow - 1; row <= corporateRow + 1; row++) {
    for (let col = corporateCol - 1; col <= corporateCol + 1; col++) {
      set(col, row, TerrainType.Building);
    }
  }

  const spawnTiles = [
    { col: corporateCol - 3, row: corporateRow + 2 },
    { col: corporateCol - 4, row: corporateRow     },
    { col: corporateCol - 2, row: corporateRow - 3 },
    { col: corporateCol + 2, row: corporateRow + 3 },
    { col: corporateCol - 5, row: corporateRow - 3 },
    { col: corporateCol - 5, row: corporateRow + 6 },
    { col: corporateCol + 4, row: corporateRow - 1 },
    { col: corporateCol + 4, row: corporateRow + 2 },
  ].filter(pos => inBounds(pos.col, pos.row));
  for (const tile of spawnTiles) set(tile.col, tile.row, TerrainType.Path);

  const corporateHQ: CorporateHQLayout = {
    buildingCol: corporateCol,
    buildingRow: corporateRow,
    accessCol:   corporateCol - 3,
    accessRow:   corporateRow + 2,
    spawnTiles,
  };

  // 9. Band spawn positions.
  const beaconCX = beaconC + 1;
  const beaconCY = beaconR + 1;
  const angleStep = (Math.PI * 2) / 6;
  const usedBandTiles = new Set<string>();
  const bandSpawnPositions: GridPosition[] = [];

  for (let i = 0; i < 6; i++) {
    let pPlaced = false;
    for (let attempt = 0; attempt < 40 && !pPlaced; attempt++) {
      const angle  = angleStep * i + (rng.next() - 0.5) * angleStep * 0.8;
      const radius = 2.5 + rng.next() * 2.5;
      const col = Math.round(beaconCX + Math.cos(angle) * radius);
      const row = Math.round(beaconCY + Math.sin(angle) * radius);
      const key = `${col},${row}`;
      if (usedBandTiles.has(key)) continue;
      if (!inBounds(col, row)) continue;
      if (!isPassable(col, row)) continue;
      if (isBeacon(col, row)) continue;
      bandSpawnPositions.push({ col, row });
      usedBandTiles.add(key);
      pPlaced = true;
    }
    if (bandSpawnPositions.length <= i) {
      const fallbacks = [
        { col: cx - 4, row: cy     },
        { col: cx + 2, row: cy - 4 },
        { col: cx - 4, row: cy - 4 },
        { col: cx + 4, row: cy     },
        { col: cx - 2, row: cy + 4 },
        { col: cx + 4, row: cy - 2 },
      ];
      bandSpawnPositions.push(fallbacks[i]);
    }
  }

  return {
    terrain,
    variant,
    hillCol,
    hillRow,
    hillRadius,
    venueLayouts,
    corporateHQ,
    bandSpawnPositions,
  };
}

// Re-export constants used by TileMap that are needed for elevation queries
// so callers only need one import site.
export { GROUND_LEVELS, ISO_TILE_WIDTH, ISO_TILE_HEIGHT, ELEVATION_STEP };
